// The scope control: which flowline(s) are in view.
//
// The model matters and is easy to get wrong. An operator always stands at a
// work centre; the chips do not change where they are, they change which of the
// lines that station feeds is in view. A station may serve four lines, or be a
// subassembly area supplying one it does not sit on. So this is a filter over
// the station, never a station picker. A leader on the floor has the same
// chips over every line on the plant.
//
// Two shapes of the same control. In the tablet header, one segmented pill;
// under the phone header, a scrolling row of chips.

import { useT } from '@/i18n/useT';

export interface ScopeOption {
  id: number;
  name: string;
  short: string;
}

export interface ScopeChipsProps {
  options: ScopeOption[];
  /** null is "All FL". */
  value: number | null;
  onChange: (flowlineId: number | null) => void;
  variant?: 'row' | 'pill';
}

export function ScopeChips({ options, value, onChange, variant = 'row' }: ScopeChipsProps) {
  const t = useT();
  // One line is not a choice. Showing "All FL · FL 1" where both mean the same
  // queue is a control that does nothing, which is worse than no control.
  if (options.length <= 1) return null;

  const items: Array<{ id: number | null; label: string }> = [
    { id: null, label: t.allFlT },
    ...options.map((o) => ({ id: o.id, label: o.short })),
  ];

  if (variant === 'pill') {
    return (
      <span
        role="group"
        aria-label={t.flowlineScopeT}
        style={{ display: 'flex', gap: 4, background: 'var(--bg)', borderRadius: 999, padding: 3 }}
      >
        {items.map((c) => {
          const selected = value === c.id;
          return (
            <button
              key={c.id ?? 'all'}
              type="button"
              className="press"
              aria-pressed={selected}
              onClick={() => onChange(c.id)}
              style={{
                borderRadius: 999,
                padding: '6px 12px',
                minHeight: 32,
                font: '700 11.5px Manrope,sans-serif',
                background: selected ? 'var(--ink)' : 'transparent',
                color: selected ? 'var(--bg)' : 'var(--sub)',
                whiteSpace: 'nowrap',
              }}
            >
              {c.label}
            </button>
          );
        })}
      </span>
    );
  }

  return (
    <div
      role="group"
      aria-label={t.flowlineScopeT}
      style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}
    >
      {items.map((c) => {
        const selected = value === c.id;
        return (
          <button
            key={c.id ?? 'all'}
            type="button"
            className="press"
            aria-pressed={selected}
            onClick={() => onChange(c.id)}
            style={{
              flex: 'none',
              borderRadius: 999,
              padding: '6px 12px',
              minHeight: 32,
              font: '700 11.5px Manrope,sans-serif',
              background: selected ? 'var(--ink)' : 'var(--surface)',
              color: selected ? 'var(--bg)' : 'var(--sub)',
              border: `1px solid ${selected ? 'var(--ink)' : 'var(--line)'}`,
              whiteSpace: 'nowrap',
            }}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}
